import { EXPERIMENT_SFW } from "@/common/services/experiment/constants";
import { GifType, IGif, INiche, IUser } from "@/common/services/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSfwStore } from "@/common/store/sfwStore";
import { apiMain } from "@/common/services";
import { hashCode } from "@/common/utils/hash";
import { loadExperiments } from "@/common/utils/storage";

const SFW_USERNAME = "safe4work";

export const useSfw = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const sfwGifs = useSfwStore((state) => state.sfwGifs);
  const sfwImages = useSfwStore((state) => state.sfwImages);
  const setSfwGifs = useSfwStore((state) => state.setSfwGifs);
  const setSfwImages = useSfwStore((state) => state.setSfwImages);
  const sfwEnabled = useMemo(() => loadExperiments()?.[EXPERIMENT_SFW] ?? false, []);

  const requestSfwGifs = useCallback(async () => {
    setLoading(true);

    try {
      const res = await apiMain.searchUserGifs({
        username: SFW_USERNAME,
        order: "new",
        count: 100
      });

      if (res.gifs.length > 0) {
        setSfwGifs(res.gifs);
        setSfwImages(res.gifs
          .filter(item => item.type === GifType.Image)
          .map(item => item.urls.thumbnail)
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const findGifReplacement = useCallback((gif: IGif) => {
    if (!sfwEnabled || sfwGifs.length === 0) {
      return null;
    }

    const replacements = sfwGifs.filter((g) => g.type === gif.type);
    const index = Math.abs(hashCode(gif.id) % replacements.length);

    return replacements[index];
  }, [sfwGifs, sfwEnabled]);

  const findImageReplacement = useCallback((url: string) => {
    if (!sfwEnabled || sfwImages.length === 0) {
      return null;
    }

    const index = Math.abs(hashCode(url) % sfwImages.length);

    return sfwImages[index];
  }, [sfwImages, sfwEnabled]);

  const sanitizeGif = useCallback((gif: IGif) => {
    const replacement = findGifReplacement(gif);

    return {
      gif: replacement ? {
        ...gif,
        urls: replacement.urls,
        width: replacement.width,
        height: replacement.height,
      } : gif,
      replacement,
    };
  }, [findGifReplacement]);

  const sanitizeUser = useCallback((user: IUser) => {
    return {
      ...user,
      profileImageUrl: user.profileImageUrl ? sanitizeImage(user.profileImageUrl) : null
    };
  }, [findImageReplacement]);

  const sanitizeNiche = useCallback((niche: INiche) => {
    const previews = niche.previews ? niche.previews.map((preview) => {
      return {
        ...preview,
        thumbnail: sanitizeImage(preview.thumbnail),
      };
    }) : [];

    const thumbnail = niche.thumbnail ? sanitizeImage(niche.thumbnail) : null;
    const cover = niche.cover ? sanitizeImage(niche.cover) : null;

    return { ...niche, previews, thumbnail, cover };
  }, [findImageReplacement]);

  const sanitizeImage = useCallback((url: string) => {
    return findImageReplacement(url) ?? url;
  }, [findImageReplacement]);

  useEffect(() => {
    if (!sfwEnabled) {
      return;
    }

    if (sfwGifs.length === 0) {
      requestSfwGifs();
    }
  }, [sfwEnabled]);

  return {
    sanitizeGif,
    sanitizeUser,
    sanitizeNiche,
    sanitizeImage,
    isLoading,
    sfwEnabled
  };
};
